import { useState } from "react";
import { toast } from "sonner";
import api from "../api";
import { formatApiError } from "../utils/formatApiError";
import Header from "./Header";
import Footer from "./Footer";
import { validatePhone } from "../utils/validatePhone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Gate component. If `user.phone` is truthy, renders children. Otherwise
// prompts for a phone number, PUTs /api/auth/phone, and calls
// onPhoneSaved(updatedUser) so the parent can update its user state — this
// component does not own user state.
//
// Props:
//   user: the current user object (must expose `.phone`)
//   onPhoneSaved(user): called after successful save with the updated user
//   onCancel(): modal variant only — called when the user dismisses the modal
//   variant: "modal" (default) or "page"
//   heading: optional heading override
//   reason: body copy shown below the heading
const RequirePhone = ({
  user,
  onPhoneSaved,
  onCancel,
  variant = "modal",
  heading = "Add your phone number",
  reason = "Please add your phone number to complete your signup. This helps event hosts reach you if needed.",
  children,
}) => {
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [saving, setSaving] = useState(false);

  if (user?.phone) return children ?? null;

  const handleSave = async () => {
    const err = validatePhone(phoneInput);
    if (err) {
      setPhoneError(err);
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("user_token");
      const res = await api.put(
        `${API}/auth/phone`,
        { phone: phoneInput },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Phone number saved!");
      onPhoneSaved?.(res.data.user);
    } catch (error) {
      setPhoneError(formatApiError(error, "Failed to save phone"));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    setPhoneInput(e.target.value);
    if (phoneError) setPhoneError("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !saving) handleSave();
  };

  const phoneIsEmpty = !phoneInput.trim();
  const form = (
    <>
      <div style={{ marginBottom: "1rem" }}>
        <label className="label">Phone Number *</label>
        <input
          type="tel"
          className="input"
          placeholder="Enter your phone number"
          value={phoneInput}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          style={phoneError ? { borderColor: "#d32f2f" } : {}}
          aria-invalid={phoneError ? "true" : "false"}
          required
          autoFocus
        />
        {phoneError && (
          <p style={{ color: "#d32f2f", fontSize: "0.8rem", marginTop: "0.25rem" }}>{phoneError}</p>
        )}
        <p style={{ fontSize: "0.75rem", color: "#888", marginTop: "0.25rem" }}>
          10-digit mobile number
        </p>
      </div>
      <button
        className="btn btn-primary"
        style={{ width: "100%" }}
        onClick={handleSave}
        disabled={saving || phoneIsEmpty}
      >
        {saving ? "Saving..." : "Save & Continue"}
      </button>
    </>
  );

  if (variant === "page") {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <Header pageTitle="One More Thing" />
        <div className="container" style={{ flex: 1, maxWidth: "768px" }}>
          <div className="card" style={{ maxWidth: "500px", margin: "0 auto" }}>
            <h2 style={{ fontSize: "1.2rem", marginBottom: "0.5rem", color: "#1B4D3E" }}>
              {heading}
            </h2>
            <p style={{ color: "#666660", fontSize: "0.9rem", marginBottom: "1.25rem", lineHeight: "1.5" }}>
              {reason}
            </p>
            {form}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "1rem"
    }}>
      <div style={{
        background: "white", borderRadius: "12px", padding: "1.5rem",
        maxWidth: "480px", width: "100%", maxHeight: "90vh", overflowY: "auto",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
          <h3 style={{ color: "#1B4D3E", margin: 0, fontSize: "1.1rem", fontWeight: "700", paddingRight: "1rem" }}>
            {heading}
          </h3>
          {onCancel && (
            <button
              onClick={onCancel}
              aria-label="Close"
              style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.4rem", color: "#666", padding: 0, lineHeight: 1, flexShrink: 0 }}
            >
              ×
            </button>
          )}
        </div>
        <p style={{ color: "#555", fontSize: "0.9rem", marginBottom: "1.25rem", lineHeight: "1.5" }}>
          {reason}
        </p>
        {form}
      </div>
    </div>
  );
};

export default RequirePhone;
